import React, { useCallback, useRef, useEffect } from "react";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { FormSection, FormSections } from "../ui";

import {
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useGetVenueByIdQuery,
} from "../../store/services/venues";
import { useTenantScope } from "../../hooks/useTenantScope";
import { yupConstants } from "../../utils/constants/yup";
import {
  venueStatusOptions,
  venueEventCategoryOptions,
  venueAmenityOptions,
} from "../../utils/constants/venues-constants";
import {
  Venue,
  VenueFetch,
  VenueFormValues,
  VenueContact,
  VenueAmenity,
  VenueEventCategory,
} from "../../models/venues";
import { mapVenue } from "../../utils/mappers";

declare global {
  interface Window {
    google?: any;
  }
}

interface VenueFormProps {
  venueData?: Venue;
  refetchVenueData?: () => void;
}

const emptyContact: VenueContact = {
  name: "",
  role: "",
  email: "",
  phone: "",
};

export const VenueForm = ({
  venueData = undefined,
  refetchVenueData,
}: VenueFormProps) => {
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const mapSearchInputRef = useRef<HTMLInputElement>(null);

  const tenantScope = useTenantScope();

  const [createVenue, { isLoading: isLoadingCreate }] = useCreateVenueMutation();
  const [updateVenue, { isLoading: isLoadingUpdate }] = useUpdateVenueMutation();

  const entityId = venueData?.id != null ? String(venueData.id) : "";
  const { data: cachedVenue, isFetching: isEntityFetching } = useGetVenueByIdQuery(
    entityId,
    { skip: !entityId }
  );
  const entity = cachedVenue ?? venueData;

  const isLoading = isLoadingCreate || isLoadingUpdate;
  const isSubmitBlocked = isEntityFetching || isLoading;

  const initialTenantIds = React.useMemo(() => {
    if (tenantScope.tenantId) {
      return [tenantScope.tenantId];
    }
    return (entity?.tenantIds || []).filter(Boolean);
  }, [entity, tenantScope.tenantId]);

  const goBack = () => navigate(-1);

  const {
    handleSubmit,
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    dirty,
  } = useFormik<VenueFormValues>({
    initialValues: {
      id: entity?.id || 0,
      name: entity?.name || "",
      address: entity?.address || "",
      latitude: entity?.latitude?.toString() || "",
      longitude: entity?.longitude?.toString() || "",
      tenantIds: initialTenantIds,
      eventCategories: entity?.eventCategories || [],
      maxCapacity: entity?.maxCapacity?.toString() || "",
      amenities: entity?.amenities || [],
      contacts: entity?.contacts?.length
        ? entity.contacts
        : [{ ...emptyContact }],
      status: entity?.status || "ACTIVE",
    },
    enableReinitialize: !!entity,
    validationSchema: Yup.object({
      name: Yup.string().required(yupConstants().required),
      address: Yup.string().required(yupConstants().required),
      latitude: Yup.number()
        .typeError("Debe ingresar un número válido")
        .required(yupConstants().required)
        .min(-90, "Latitud debe estar entre -90 y 90")
        .max(90, "Latitud debe estar entre -90 y 90"),
      longitude: Yup.number()
        .typeError("Debe ingresar un número válido")
        .required(yupConstants().required)
        .min(-180, "Longitud debe estar entre -180 y 180")
        .max(180, "Longitud debe estar entre -180 y 180"),
      status: Yup.string().required(yupConstants().required),
      contacts: Yup.array().of(
        Yup.object({
          name: Yup.string(),
          role: Yup.string(),
          email: Yup.string().email(yupConstants().email),
          phone: Yup.string(),
        })
      ),
    }),
    onSubmit: (values) => {
      if (isEntityFetching) return;

      const payload: VenueFetch = {
        id: values.id || undefined,
        name: values.name,
        address: values.address,
        latitude: parseFloat(values.latitude),
        longitude: parseFloat(values.longitude),
        tenantIds: tenantScope.tenantId
          ? [tenantScope.tenantId]
          : values.tenantIds.filter(Boolean),
        eventCategories: values.eventCategories,
        maxCapacity: values.maxCapacity ? parseInt(values.maxCapacity) : null,
        amenities: values.amenities,
        contacts: values.contacts.filter((c) => c.name && c.email),
        status: values.status,
      };

      if (venueData) {
        updateVenue(payload)
          .unwrap()
          .then(() => {
            toast.success("Venue modificado exitosamente!");
            navigate(-1);
          })
          .catch((err) => {
            const errorMessage =
              err?.data?.error?.message ||
              err?.data?.errorMessage ||
              "No se pudo modificar el venue";
            toast.error(errorMessage);
          });
      } else {
        if (!tenantScope.tenantId) return;
        createVenue(payload)
          .unwrap()
          .then((created) => {
            const createdVenue = mapVenue({ ...payload, id: created.id });
            toast.success("Venue creado exitosamente!");
            navigate("/venues", { state: { createdVenue } });
          })
          .catch((err) => {
            const errorMessage =
              err?.data?.error?.message ||
              err?.data?.errorMessage ||
              "No se pudo crear el venue";
            toast.error(errorMessage);
          });
      }
    },
  });

  // Avisa antes de que un cambio de tenant en el header recargue la pantalla
  // y se lleve puesto lo que se estaba cargando.
  useUnsavedChangesGuard(dirty);

  useEffect(() => {
    if (!tenantScope.tenantId) {
      return;
    }
    const alreadyScoped =
      values.tenantIds.length === 1 && values.tenantIds[0] === tenantScope.tenantId;
    if (!alreadyScoped) {
      setFieldValue("tenantIds", [tenantScope.tenantId]);
    }
  }, [tenantScope.tenantId, values.tenantIds, setFieldValue]);

  // ── Google Maps ──
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const lat = parseFloat(values.latitude) || -34.6037;
    const lng = parseFloat(values.longitude) || -58.3816;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 15,
      mapId: "venue-map",
    });

    googleMapRef.current = map;

    // Use AdvancedMarkerElement if available, fallback to Marker
    if (window.google.maps.marker?.AdvancedMarkerElement) {
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat, lng },
        gmpDraggable: true,
        title: "Ubicación del Venue",
      });

      marker.addListener("dragend", () => {
        const pos = marker.position;
        if (pos) {
          setFieldValue("latitude", pos.lat.toFixed(6));
          setFieldValue("longitude", pos.lng.toFixed(6));
        }
      });

      markerRef.current = marker;
    }

    map.addListener("click", (e: any) => {
      const latLng = e.latLng;
      if (!latLng) return;
      const newLat = latLng.lat();
      const newLng = latLng.lng();
      setFieldValue("latitude", newLat.toFixed(6));
      setFieldValue("longitude", newLng.toFixed(6));

      if (markerRef.current) {
        try {
          markerRef.current.position = { lat: newLat, lng: newLng };
        } catch (_) {}
      }
    });

    // Places Autocomplete on address field
    if (addressInputRef.current && window.google.maps.places) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        fields: ["formatted_address", "geometry"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry?.location) return;

        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();

        setFieldValue("latitude", newLat.toFixed(6));
        setFieldValue("longitude", newLng.toFixed(6));
        if (place.formatted_address) {
          setFieldValue("address", place.formatted_address);
        }

        map.setCenter({ lat: newLat, lng: newLng });
        map.setZoom(17);

        if (markerRef.current) {
          try {
            markerRef.current.position = { lat: newLat, lng: newLng };
          } catch (_) {}
        }
      });
    }

    // SearchBox inside the map for coordinate search
    if (mapSearchInputRef.current && window.google.maps.places) {
      const searchBox = new window.google.maps.places.SearchBox(mapSearchInputRef.current);
      map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(mapSearchInputRef.current);

      searchBox.addListener("places_changed", () => {
        const places = searchBox.getPlaces();
        if (!places || places.length === 0) return;

        const place = places[0];
        if (!place.geometry?.location) return;

        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();

        setFieldValue("latitude", newLat.toFixed(6));
        setFieldValue("longitude", newLng.toFixed(6));

        map.setCenter({ lat: newLat, lng: newLng });
        map.setZoom(17);

        if (markerRef.current) {
          try {
            markerRef.current.position = { lat: newLat, lng: newLng };
          } catch (_) {}
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    if (window.google?.maps) {
      initMap();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=marker,places`;
    script.async = true;
    script.defer = true;
    script.onload = () => initMap();
    document.head.appendChild(script);
  }, [initMap]);

  // Update marker when lat/lng changes via form input
  useEffect(() => {
    const lat = parseFloat(values.latitude);
    const lng = parseFloat(values.longitude);
    if (!isNaN(lat) && !isNaN(lng) && markerRef.current && googleMapRef.current) {
      try {
        markerRef.current.position = { lat, lng };
        googleMapRef.current.panTo({ lat, lng });
      } catch (_) {}
    }
  }, [values.latitude, values.longitude]);

  // ── Contact Management ──
  const addContact = () => {
    setFieldValue("contacts", [...values.contacts, { ...emptyContact }]);
  };

  const removeContact = (index: number) => {
    if (values.contacts.length <= 1) return;
    const updated = values.contacts.filter((_, i) => i !== index);
    setFieldValue("contacts", updated);
  };

  const updateContact = (index: number, field: keyof VenueContact, value: string) => {
    const updated = [...values.contacts];
    updated[index] = { ...updated[index], [field]: value };
    setFieldValue("contacts", updated);
  };

  // ── Amenity toggle ──
  const toggleAmenity = (amenity: VenueAmenity) => {
    const current = values.amenities;
    if (current.includes(amenity)) {
      setFieldValue(
        "amenities",
        current.filter((a) => a !== amenity)
      );
    } else {
      setFieldValue("amenities", [...current, amenity]);
    }
  };

  // ── Event Category toggle ──
  const toggleCategory = (category: VenueEventCategory) => {
    const current = values.eventCategories;
    if (current.includes(category)) {
      setFieldValue(
        "eventCategories",
        current.filter((c) => c !== category)
      );
    } else {
      setFieldValue("eventCategories", [...current, category]);
    }
  };

  const getContactError = (index: number, field: keyof VenueContact): string | undefined => {
    const contactErrors = errors.contacts as any;
    const contactTouched = touched.contacts as any;
    if (contactErrors?.[index]?.[field] && contactTouched?.[index]?.[field]) {
      return contactErrors[index][field];
    }
    return undefined;
  };

  return (
    <form onSubmit={handleSubmit} className="p-fluid">
      <FormSections>
      {/* ========================================= */}
      {/* Información Básica                        */}
      {/* ========================================= */}
      <FormSection title="Información Básica">
        <div className="grid">
          <div className="col-12 md:col-6 field">
            <label htmlFor="name" className="font-medium text-900 block mb-2">
              Nombre del Venue *
            </label>
            <InputText
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={touched.name && errors.name ? "p-invalid" : ""}
              disabled={isLoading}
            />
            {touched.name && errors.name && (
              <small className="p-error">{errors.name}</small>
            )}
          </div>

          <div className="col-12 md:col-6 field">
            <label htmlFor="address" className="font-medium text-900 block mb-2">
              Dirección completa *
            </label>
            <input
              ref={addressInputRef}
              id="address"
              name="address"
              type="text"
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`p-inputtext p-component w-full${touched.address && errors.address ? " p-invalid" : ""}`}
              disabled={isLoading}
              placeholder="Escribí una dirección o elegí una sugerencia"
            />
            {touched.address && errors.address && (
              <small className="p-error">{errors.address}</small>
            )}
          </div>

          <div className="col-12 md:col-6 field">
            <label htmlFor="latitude" className="font-medium text-900 block mb-2">
              Latitud *
            </label>
            <InputText
              id="latitude"
              name="latitude"
              value={values.latitude}
              onChange={handleChange}
              onBlur={handleBlur}
              className={touched.latitude && errors.latitude ? "p-invalid" : ""}
              disabled={isLoading}
              placeholder="-34.603722"
            />
            {touched.latitude && errors.latitude && (
              <small className="p-error">{errors.latitude}</small>
            )}
          </div>

          <div className="col-12 md:col-6 field">
            <label htmlFor="longitude" className="font-medium text-900 block mb-2">
              Longitud *
            </label>
            <InputText
              id="longitude"
              name="longitude"
              value={values.longitude}
              onChange={handleChange}
              onBlur={handleBlur}
              className={touched.longitude && errors.longitude ? "p-invalid" : ""}
              disabled={isLoading}
              placeholder="-58.381592"
            />
            {touched.longitude && errors.longitude && (
              <small className="p-error">{errors.longitude}</small>
            )}
          </div>

          {/* Map */}
          <div className="col-12 field">
            <label className="font-medium text-900 block mb-2">
              Mapa — Buscá un lugar, hacé click o arrastrá el marcador
            </label>
            <input
              ref={mapSearchInputRef}
              type="text"
              placeholder="Buscar en el mapa..."
              style={{
                width: "300px",
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                marginBottom: "8px",
                outline: "none",
              }}
            />
            <div
              ref={mapRef}
              style={{ width: "100%", height: "400px", borderRadius: "8px", border: "1px solid #dee2e6" }}
            >
              {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                <div className="flex align-items-center justify-content-center h-full text-color-secondary">
                  <div className="text-center">
                    <i className="pi pi-map-marker text-4xl mb-2" />
                    <p>Configure REACT_APP_GOOGLE_MAPS_API_KEY para habilitar el mapa</p>
                    <p className="text-sm">Puede ingresar las coordenadas manualmente arriba</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </FormSection>

      {/* ========================================= */}
      {/* Información Operativa                     */}
      {/* ========================================= */}
      <FormSection title="Información Operativa">
        <div className="grid">
          {/* Tipo de evento / Categoría */}
          <div className="col-12 field">
            <label className="font-medium text-900 block mb-2">
              Tipo de evento (Categoría)
            </label>
            <div className="flex flex-wrap gap-3">
              {venueEventCategoryOptions.map((cat) => (
                <div key={cat.value} className="flex align-items-center">
                  <Checkbox
                    inputId={`cat-${cat.value}`}
                    checked={values.eventCategories.includes(cat.value)}
                    onChange={() => toggleCategory(cat.value)}
                    disabled={isLoading}
                  />
                  <label htmlFor={`cat-${cat.value}`} className="ml-2">
                    {cat.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="col-12 md:col-6 field">
            <label htmlFor="maxCapacity" className="font-medium text-900 block mb-2">
              Capacidad máxima
            </label>
            <InputNumber
              id="maxCapacity"
              value={values.maxCapacity ? parseInt(values.maxCapacity) : null}
              onValueChange={(e) =>
                setFieldValue("maxCapacity", e.value?.toString() || "")
              }
              min={0}
              disabled={isLoading}
              placeholder="Ej: 200"
            />
          </div>

          <div className="col-12 md:col-6 field">
            <label htmlFor="status" className="font-medium text-900 block mb-2">
              Estado *
            </label>
            <Dropdown
              id="status"
              value={values.status}
              options={venueStatusOptions}
              optionLabel="name"
              optionValue="value"
              onChange={(e) => setFieldValue("status", e.value)}
              className={touched.status && errors.status ? "p-invalid" : ""}
              disabled={isLoading}
            />
            {touched.status && errors.status && (
              <small className="p-error">{errors.status}</small>
            )}
          </div>

          {/* Amenities */}
          <div className="col-12 field">
            <label className="font-medium text-900 block mb-2">Amenities</label>
            <div className="flex flex-wrap gap-3">
              {venueAmenityOptions.map((amenity) => (
                <div key={amenity.value} className="flex align-items-center">
                  <Checkbox
                    inputId={`amenity-${amenity.value}`}
                    checked={values.amenities.includes(amenity.value)}
                    onChange={() => toggleAmenity(amenity.value)}
                    disabled={isLoading}
                  />
                  <label htmlFor={`amenity-${amenity.value}`} className="ml-2">
                    {amenity.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </FormSection>

      {/* ========================================= */}
      {/* Contactos                                 */}
      {/* ========================================= */}
      <FormSection title="Contactos">
        {values.contacts.map((contact, index) => (
          <div key={index} className="grid align-items-end mb-3">
            <div className="col-12">
              <Divider align="left">
                <span className="flex align-items-center gap-2">
                  <span className="text-sm font-semibold text-color-secondary">
                    {index === 0 ? "Contacto Principal" : `Contacto #${index + 1}`}
                  </span>
                  {index > 0 && (
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      severity="danger"
                      outlined
                      onClick={() => removeContact(index)}
                      disabled={isLoading}
                      tooltip="Eliminar contacto"
                    />
                  )}
                </span>
              </Divider>
            </div>
            <div className="col-12 md:col-6 lg:col-3 field">
              <label className="font-medium text-900 block mb-2">Nombre</label>
              <InputText
                value={contact.name}
                onChange={(e) => updateContact(index, "name", e.target.value)}
                className={getContactError(index, "name") ? "p-invalid" : ""}
                disabled={isLoading}
              />
              {getContactError(index, "name") && (
                <small className="p-error">{getContactError(index, "name")}</small>
              )}
            </div>
            <div className="col-12 md:col-6 lg:col-3 field">
              <label className="font-medium text-900 block mb-2">Rol</label>
              <InputText
                value={contact.role}
                onChange={(e) => updateContact(index, "role", e.target.value)}
                className={getContactError(index, "role") ? "p-invalid" : ""}
                disabled={isLoading}
              />
              {getContactError(index, "role") && (
                <small className="p-error">{getContactError(index, "role")}</small>
              )}
            </div>
            <div className="col-12 md:col-6 lg:col-3 field">
              <label className="font-medium text-900 block mb-2">Email</label>
              <InputText
                value={contact.email}
                onChange={(e) => updateContact(index, "email", e.target.value)}
                className={getContactError(index, "email") ? "p-invalid" : ""}
                disabled={isLoading}
                type="email"
              />
              {getContactError(index, "email") && (
                <small className="p-error">{getContactError(index, "email")}</small>
              )}
            </div>
            <div className="col-12 md:col-6 lg:col-3 field">
              <label className="font-medium text-900 block mb-2">Teléfono</label>
              <InputText
                value={contact.phone}
                onChange={(e) => updateContact(index, "phone", e.target.value)}
                className={getContactError(index, "phone") ? "p-invalid" : ""}
                disabled={isLoading}
              />
              {getContactError(index, "phone") && (
                <small className="p-error">{getContactError(index, "phone")}</small>
              )}
            </div>
          </div>
        ))}
        <Button
          type="button"
          label="Agregar contacto"
          icon="pi pi-plus"
          outlined
          onClick={addContact}
          disabled={isLoading}
          className="mt-2"
        />
      </FormSection>

      {/* ========================================= */}
      {/* Métricas de calidad (solo lectura)        */}
      {/* ========================================= */}
      {venueData && (
        <FormSection title="Métricas de Calidad (Solo lectura)">
          <div className="grid">
            <div className="col-12 sm:col-6 lg:col-3">
              <div className="text-color-secondary text-sm mb-1">Calificación promedio</div>
              <div className="flex align-items-center gap-2">
                <i className="pi pi-star-fill text-yellow-500 text-xl" />
                <span className="text-2xl font-bold">
                  {venueData.rating?.average?.toFixed(1) || "—"}
                </span>
                <span className="text-color-secondary">
                  ({venueData.rating?.count || 0} valoraciones)
                </span>
              </div>
            </div>
            <div className="col-12">
              <div className="text-color-secondary text-sm mb-2">Últimos comentarios</div>
              {venueData.comments && venueData.comments.length > 0 ? (
                <div className="flex flex-column gap-2">
                  {venueData.comments.slice(0, 10).map((comment) => (
                    <div key={comment.id} className="surface-100 border-round p-3">
                      <div className="flex justify-content-between align-items-center mb-1">
                        <span className="font-semibold">{comment.userName}</span>
                        <div className="flex align-items-center gap-1">
                          <i className="pi pi-star-fill text-yellow-500 text-sm" />
                          <span>{comment.rating}</span>
                        </div>
                      </div>
                      <p className="m-0 text-color-secondary">{comment.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-color-secondary">No hay comentarios aún.</p>
              )}
            </div>
          </div>
        </FormSection>
      )}
      </FormSections>

      {/* ========================================= */}
      {/* Botones de acción                         */}
      {/* ========================================= */}
      <div className="flex justify-content-end gap-3 mt-4">
        <Button
          type="button"
          label="Cancelar"
          icon="pi pi-times"
          outlined
          onClick={goBack}
          disabled={isLoading}
        />
        <Button
          type="submit"
          label={venueData ? "Guardar cambios" : "Crear Venue"}
          icon="pi pi-check"
          disabled={isSubmitBlocked}
          loading={isLoading}
        />
      </div>
    </form>
  );
};
